var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";
import i18n from "../../i18n";
import { API_BASE_URL } from "../../config/app-config";
import { pca, msalReady } from "../../auth/msal";
import { loginRequest } from "../../auth/auth-config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
const DEBUG_PREFIX = "[API-AUTH]";
const mutex = new Mutex();
// Track consecutive 401s to prevent infinite redirect loops
let consecutive401Count = 0;
const MAX_401_RETRIES = 2;
function acquireMsalToken() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        try {
            // Wait for MSAL to be fully initialized AND redirect to be processed
            yield msalReady;
            const accounts = pca.getAllAccounts();
            const account = pca.getActiveAccount() || (accounts === null || accounts === void 0 ? void 0 : accounts[0]);
            if (!account) {
                console.warn(DEBUG_PREFIX, "acquireMsalToken: No MSAL account found");
                return null;
            }
            const result = yield pca.acquireTokenSilent(Object.assign(Object.assign({}, loginRequest), { account }));
            return (_a = result === null || result === void 0 ? void 0 : result.accessToken) !== null && _a !== void 0 ? _a : null;
        }
        catch (err) {
            console.error(DEBUG_PREFIX, "acquireMsalToken: Silent token FAILED:", err);
            if (err instanceof InteractionRequiredAuthError) {
                console.warn(DEBUG_PREFIX, "acquireMsalToken: InteractionRequired — redirecting to login page instead of acquireTokenRedirect to prevent loop");
                // Instead of calling acquireTokenRedirect (which causes loops),
                // redirect to login page so user can re-authenticate cleanly
                try {
                    window.location.replace("/login");
                }
                catch (_b) { }
            }
            return null;
        }
    });
}
const rawBaseQuery = fetchBaseQuery({ baseUrl: API_BASE_URL });
function toFetchArgs(args) {
    if (typeof args === "string")
        return { url: args };
    return args;
}
function withAuthHeaders(args) {
    return __awaiter(this, void 0, void 0, function* () {
        const next = toFetchArgs(args);
        const headers = new Headers(next.headers || {});
        headers.set("Accept-Language", i18n.language || "en");
        const token = yield acquireMsalToken();
        if (token) {
            headers.set("Authorization", `Bearer ${token}`);
        }
        else {
            console.warn(DEBUG_PREFIX, "withAuthHeaders: No token available for request:", next.url);
        }
        return Object.assign(Object.assign({}, next), { headers });
    });
}
export const baseQueryWithMsal = (args, api, extraOptions) => __awaiter(void 0, void 0, void 0, function* () {
    yield mutex.waitForUnlock();
    const authedArgs = yield withAuthHeaders(args);
    let result = yield rawBaseQuery(authedArgs, api, extraOptions);
    if (result.error && result.error.status === 401) {
        consecutive401Count++;
        const url = typeof args === "string" ? args : args.url;
        console.warn(DEBUG_PREFIX, `401 received for ${url} (consecutive: ${consecutive401Count}/${MAX_401_RETRIES})`);
        if (consecutive401Count > MAX_401_RETRIES) {
            console.error(DEBUG_PREFIX, `Max 401 retries (${MAX_401_RETRIES}) exceeded — redirecting to /login to break loop`);
            consecutive401Count = 0;
            try {
                window.location.replace("/login");
            }
            catch (_a) { }
            return result;
        }
        if (!mutex.isLocked()) {
            const release = yield mutex.acquire();
            try {
                yield acquireMsalToken();
                const retryArgs = yield withAuthHeaders(args);
                result = yield rawBaseQuery(retryArgs, api, extraOptions);
                if (result.error && result.error.status === 401) {
                    console.error(DEBUG_PREFIX, "Retry still returned 401 — redirecting to /login");
                    try {
                        window.location.replace("/login");
                    }
                    catch (_b) { }
                }
                else {
                    // Success — reset counter
                    consecutive401Count = 0;
                }
            }
            finally {
                release();
            }
        }
        else {
            yield mutex.waitForUnlock();
            const retryArgs = yield withAuthHeaders(args);
            result = yield rawBaseQuery(retryArgs, api, extraOptions);
            if (!result.error || result.error.status !== 401) {
                consecutive401Count = 0;
            }
        }
    }
    else if (!result.error) {
        // Successful request — reset counter
        consecutive401Count = 0;
    }
    return result;
});
